import { DataDate } from "./data_date";
import { Series, SeriesValue } from "./series";

// 季節指数の統計情報
export interface SeasonalIndexStatistics {
  peak_month: number | null; // 最も高い月（1-12）
  average_ratio: number; // 年平均に対する倍率
}

// エンドポイントから返されるデータの型定義
export interface SeasonalPeriodicityData {
  name: string;
  values: Array<[string, number]>;
  seasonal_index?: SeasonalIndexStatistics; // 季節指数の統計情報
}

// 統計情報の型定義
export interface SeasonalPeriodicityStatistics {
  increaseRate: number; // 先月までの1年間と1年前の1年間の比較した上昇率（%）
  trend: "急激に上昇がみられます" | "上昇傾向にあります" | "横ばい傾向です" | "減少傾向にあります" | "急激に減少が見られます"; // 直近3年の傾向
  annualSlope: number; // 年率換算の傾き（%）
}

class SeasonalPeriodicitySeries {
  /**
   * エンドポイントから取得したデータをSeries形式に変換
   */
  static convertToSeries(
    data: SeasonalPeriodicityData,
  ): Series {
    const values: SeriesValue[] = data.values.map(([date, value]) => [
      date,
      value,
    ]);

    // 最初の日付をoriginとして使用
    const parsedOrigin = values.length > 0
      ? DataDate.parseYMD(values[0][0], "monthly")
      : null;
    const origin = parsedOrigin || undefined;

    return new Series(values, data.name, origin);
  }

  /**
   * データを年ごとに分割し、各年をX軸の範囲（最初のデータの月から12ヶ月分）にマッピングしたSeriesの配列を返す
   * データの先頭から36ヶ月分を取得し、12ヶ月ずつ3分割する
   * @param data エンドポイントから取得したデータ
   * @returns 年ごとのSeriesの配列（[3年前, 2年前, 今年]の順）とX軸の開始月情報
   */
  static convertToYearlySeries(
    data: SeasonalPeriodicityData,
  ): {
    yearlySeries: Array<{ series: Series; year: number; label: string }>;
    axisStartYear: number;
    axisStartMonth: number;
    axisEndYear: number;
    axisEndMonth: number;
  } {
    const values = data.values || [];
    if (values.length === 0) {
      return {
        yearlySeries: [],
        axisStartYear: 0,
        axisStartMonth: 1,
        axisEndYear: 0,
        axisEndMonth: 12,
      };
    }

    // データを日付順にソート
    const sortedData = [...values].sort(
      (a, b) =>
        new Date(a[0]).getTime() -
        new Date(b[0]).getTime(),
    );

    // データの先頭から36ヶ月分を取得（データが足りない場合は可能な範囲で）
    const maxMonths = Math.min(36, sortedData.length);
    const dataForThreeYears = sortedData.slice(0, maxMonths);

    // 最初の12ヶ月、次の12ヶ月、最後の12ヶ月に分割
    const firstYearData = dataForThreeYears.slice(0, 12);
    const secondYearData = dataForThreeYears.slice(12, 24);
    const thirdYearData = dataForThreeYears.slice(24, 36);

    // 3分割したうちの一番最後の年（今年）を取得（ラベル用）
    const getLastYear = (): number | null => {
      if (thirdYearData.length === 0) {
        // thirdYearDataが空の場合は、secondYearDataから取得
        if (secondYearData.length === 0) {
          if (firstYearData.length === 0) return null;
          const firstDate = new Date(firstYearData[firstYearData.length - 1][0]);
          return firstDate.getFullYear();
        }
        const lastDate = new Date(secondYearData[secondYearData.length - 1][0]);
        return lastDate.getFullYear();
      }
      const lastDate = new Date(thirdYearData[thirdYearData.length - 1][0]);
      return lastDate.getFullYear();
    };

    // X軸は常に1月から12月の固定12ヶ月
    const axisStartMonth = 1;
    const axisEndMonth = 12;
    // X軸の年は最後の年（今年）を使用（表示用）
    const lastYear = getLastYear();
    const axisStartYear = lastYear || new Date().getFullYear();
    const axisEndYear = axisStartYear;

    // 各年のデータを取得（3年前、2年前、今年の順）
    const yearlySeries: Array<{ series: Series; year: number; label: string }> = [];
    const yearDataArrays = [firstYearData, secondYearData, thirdYearData];

    if (!lastYear) {
      return {
        yearlySeries: [],
        axisStartYear,
        axisStartMonth,
        axisEndYear,
        axisEndMonth,
      };
    }

    // 各年の最初のデータの年を取得
    const getYearFromData = (yearData: Array<[string, number]>): number | null => {
      if (yearData.length === 0) return null;
      const firstDate = new Date(yearData[0][0]);
      return firstDate.getFullYear();
    };

    for (let yearIndex = 0; yearIndex < 3; yearIndex++) {
      const yearData = yearDataArrays[yearIndex];
      if (yearData.length === 0) continue;

      const year = getYearFromData(yearData);
      if (!year) continue;

      // X軸の12ヶ月分にマッピング
      const mappedData: SeriesValue[] = [];
      let lastValue = 0;

      // X軸は1月から12月の固定12ヶ月
      for (let month = 1; month <= 12; month++) {
        // 各年の実際の年と月を使用して日付文字列を作成
        const axisDateStr = `${year}-${String(month).padStart(2, '0')}-01`;

        // このX軸の位置（1-12月）に対応する、各年の同じ月のデータを検索
        const foundData = yearData.find(([dateStr]) => {
          const pointDate = new Date(dateStr);
          return pointDate.getMonth() + 1 === month;
        });

        if (foundData) {
          mappedData.push([axisDateStr, foundData[1]]);
          lastValue = foundData[1];
        } else {
          // データが存在しない月は、前月の値を使用（または0）
          mappedData.push([axisDateStr, lastValue]);
        }
      }

      if (mappedData.length > 0) {
        // X軸の開始月（1月）をoriginとして設定（各年の1月を使用）
        const parsedOrigin = DataDate.parseYMD(`${year}-01-01`, "monthly");
        const origin = parsedOrigin || undefined;

        // ラベルを生成：3分割したうちの一番最後の年（今年）を基準に相対的なラベルを表示
        // yearIndex: 0=3年前, 1=2年前, 2=今年
        let label: string;
        if (yearIndex === 2) {
          // 今年
          label = `${lastYear}年`;
        } else if (yearIndex === 1) {
          // 2年前
          label = `${lastYear - 1}年`;
        } else {
          // 3年前
          label = `${lastYear - 2}年`;
        }

        const series = new Series(mappedData, label, origin);
        yearlySeries.push({
          series,
          year,
          label,
        });
      }
    }

    // 順序を逆にして[今年, 2年前, 3年前]にする
    const reversedYearlySeries = yearlySeries.reverse();

    return {
      yearlySeries: reversedYearlySeries,
      axisStartYear,
      axisStartMonth,
      axisEndYear,
      axisEndMonth,
    };
  }

  /**
   * 先月と1年前の先月を比較した上昇率を計算
   * @param data エンドポイントから取得したデータ
   * @returns 上昇率（%）
   */
  static calculateIncreaseRate(
    data: SeasonalPeriodicityData,
  ): number {
    const values = data.values || [];
    if (values.length === 0) {
      return 0;
    }

    // データを日付順にソート
    const sortedData = [...values].sort(
      (a, b) =>
        new Date(a[0]).getTime() -
        new Date(b[0]).getTime(),
    );

    // 最後の月を取得
    const lastDateStr = sortedData[sortedData.length - 1][0];
    const lastDate = new Date(lastDateStr);

    // 先月（最後の月の1つ前）を計算
    const previousMonth = new Date(
      lastDate.getFullYear(),
      lastDate.getMonth() - 1,
      1,
    );

    // 1年前の先月を計算
    const previousYearPreviousMonth = new Date(
      lastDate.getFullYear() - 1,
      lastDate.getMonth() - 1,
      1,
    );

    // 先月のデータを取得
    const previousMonthData = sortedData.find(([dateStr]) => {
      const pointDate = new Date(dateStr);
      const pointMonthStart = new Date(
        pointDate.getFullYear(),
        pointDate.getMonth(),
        1,
      );
      return (
        pointMonthStart.getTime() === previousMonth.getTime()
      );
    });

    // 1年前の先月のデータを取得
    const previousYearPreviousMonthData = sortedData.find(([dateStr]) => {
      const pointDate = new Date(dateStr);
      const pointMonthStart = new Date(
        pointDate.getFullYear(),
        pointDate.getMonth(),
        1,
      );
      return (
        pointMonthStart.getTime() === previousYearPreviousMonth.getTime()
      );
    });

    // 各月の値を取得
    const previousMonthValue = previousMonthData ? previousMonthData[1] : null;
    const previousYearPreviousMonthValue = previousYearPreviousMonthData
      ? previousYearPreviousMonthData[1]
      : null;

    // データが不足している場合は0を返す
    if (previousMonthValue === null || previousYearPreviousMonthValue === null) {
      return 0;
    }

    // 上昇率を計算
    if (previousYearPreviousMonthValue === 0) {
      return previousMonthValue > 0 ? 100 : 0;
    }

    return (
      ((previousMonthValue - previousYearPreviousMonthValue) /
        previousYearPreviousMonthValue) *
      100
    );
  }

  /**
   * 直近3年の傾向を判定（年率換算で判定）
   * @param data エンドポイントから取得したデータ
   * @returns トレンド文字列と年率換算の傾き
   */
  static calculateTrend(
    data: SeasonalPeriodicityData,
  ): {
    trend: "急激に上昇がみられます" | "上昇傾向にあります" | "横ばい傾向です" | "減少傾向にあります" | "急激に減少が見られます";
    annualSlope: number;
  } {
    const values = data.values || [];
    if (values.length < 2) {
      return { trend: "横ばい傾向です", annualSlope: 0 };
    }

    // データを日付順にソート
    const sortedData = [...values].sort(
      (a, b) =>
        new Date(a[0]).getTime() -
        new Date(b[0]).getTime(),
    );

    // 最後の月を取得（YYYY-MM-01形式に正規化）
    const lastDateStr = sortedData[sortedData.length - 1][0];
    const lastDate = new Date(lastDateStr);
    const lastMonthStart = new Date(
      lastDate.getFullYear(),
      lastDate.getMonth(),
      1,
    );

    // 3年前の開始日（36ヶ月前の月初）
    const threeYearsAgoStart = new Date(lastMonthStart);
    threeYearsAgoStart.setMonth(threeYearsAgoStart.getMonth() - 36);

    // 直近3年のデータを取得（最後の月を含む）
    const threeYearData = sortedData.filter(([dateStr]) => {
      const pointDate = new Date(dateStr);
      const pointMonthStart = new Date(
        pointDate.getFullYear(),
        pointDate.getMonth(),
        1,
      );
      return pointMonthStart >= threeYearsAgoStart && pointMonthStart <= lastMonthStart;
    });

    if (threeYearData.length < 2) {
      return { trend: "横ばい傾向です", annualSlope: 0 };
    }

    // 線形回帰で傾向を判定
    const n = threeYearData.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    threeYearData.forEach(([, value], index) => {
      const x = index;
      const y = value;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const monthlySlope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

    // 最初の値（基準値）を取得
    const firstValue = threeYearData[0][1];

    // 年率換算の傾きを計算（月次の傾きを12倍し、基準値に対する割合を計算）
    // 月次の傾きは1ヶ月あたりの変化量なので、年率換算は (monthlySlope * 12 / firstValue) * 100
    const annualSlope = firstValue !== 0
      ? (monthlySlope * 12 / firstValue) * 100
      : 0;

    // 傾きの閾値に基づいてトレンドを判定
    if (annualSlope >= 30) {
      return { trend: "急激に上昇がみられます", annualSlope };
    } else if (annualSlope >= 10) {
      return { trend: "上昇傾向にあります", annualSlope };
    } else if (annualSlope > -10) {
      return { trend: "横ばい傾向です", annualSlope };
    } else if (annualSlope >= -30) {
      return { trend: "減少傾向にあります", annualSlope };
    } else {
      return { trend: "急激に減少が見られます", annualSlope };
    }
  }

  /**
   * 統計情報を計算
   */
  static calculateStatistics(
    data: SeasonalPeriodicityData,
  ): SeasonalPeriodicityStatistics {
    const trendResult = this.calculateTrend(data);
    return {
      increaseRate: this.calculateIncreaseRate(data),
      trend: trendResult.trend,
      annualSlope: trendResult.annualSlope,
    };
  }

  /**
   * 線形回帰のパラメータを取得（グラフ描画用）
   * @param data エンドポイントから取得したデータ
   * @returns 線形回帰の切片と傾き、およびデータ範囲（日付文字列）
   */
  static getLinearRegressionParams(
    data: SeasonalPeriodicityData,
  ): {
    intercept: number;
    slope: number;
    startDate: string;
    endDate: string;
  } | null {
    const values = data.values || [];
    if (values.length < 2) {
      return null;
    }

    // データを日付順にソート
    const sortedData = [...values].sort(
      (a, b) =>
        new Date(a[0]).getTime() -
        new Date(b[0]).getTime(),
    );

    // 最後の月を取得
    const lastDateStr = sortedData[sortedData.length - 1][0];
    const lastDate = new Date(lastDateStr);
    const lastMonthStart = new Date(
      lastDate.getFullYear(),
      lastDate.getMonth(),
      1,
    );

    // 3年前の開始日（36ヶ月前の月初）
    const threeYearsAgoStart = new Date(lastMonthStart);
    threeYearsAgoStart.setMonth(threeYearsAgoStart.getMonth() - 36);

    // 直近3年のデータを取得
    const threeYearData: Array<[string, number]> = [];
    const allIndices: number[] = [];

    sortedData.forEach(([dateStr, value], index) => {
      const pointDate = new Date(dateStr);
      const pointMonthStart = new Date(
        pointDate.getFullYear(),
        pointDate.getMonth(),
        1,
      );
      if (pointMonthStart >= threeYearsAgoStart && pointMonthStart <= lastMonthStart) {
        threeYearData.push([dateStr, value]);
        allIndices.push(index);
      }
    });

    if (threeYearData.length < 2) {
      return null;
    }

    // 線形回帰を計算
    const n = threeYearData.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    threeYearData.forEach(([, value], index) => {
      const x = index;
      const y = value;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return {
      intercept,
      slope,
      startDate: threeYearData[0][0],
      endDate: threeYearData[threeYearData.length - 1][0],
    };
  }
}

export { SeasonalPeriodicitySeries };

